import { translate } from "@/i18n/i18n.config";
import type { RouteNamedMap } from "@/router/types";
import {
  AppEnum,
  getItem,
  showItemNode,
  type Item,
  type ItemNodeItemTypeEnum,
} from "@/services/internalApiV1";
import { FlashKindEnum, flash } from "@/utils/flash";
import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";

import {
  getItemNodePublicRoute,
  itemNodeTypeToRouteMapping,
} from "./itemNodeRoutes";

const SHARED_LIBRARY_PATH = "/shared-folders";
// Only a successful permission decision validates a route. Merely matching
// the same item is insufficient after token entry or a failed lookup.
const validatedEditors = new WeakSet<RouteLocationNormalized>();

async function libraryPath(
  item: Item,
  from: RouteLocationNormalized
): Promise<string> {
  try {
    const node = await showItemNode(item.itemNodeId);
    const library = node.library;
    if (!library || library.id !== item.libraryId) return SHARED_LIBRARY_PATH;
    const school = String(library.companyId);
    if (
      (from.path === SHARED_LIBRARY_PATH ||
        from.path.startsWith(`${SHARED_LIBRARY_PATH}/`)) &&
      from.query.school === school
    ) {
      return from.fullPath;
    }
    return `${SHARED_LIBRARY_PATH}?school=${school}`;
  } catch {
    // Missing library details must not prevent the preview redirect.
    return SHARED_LIBRARY_PATH;
  }
}

// `ai_assistant-show` is the mapping's link target, not an editor: the route
// table redirects it onto `ai_chat-assistants-show` before any guard runs
// (#13751), so the guard only ever sees the editor itself, listed below.
const NOT_AN_EDITOR: ReadonlySet<keyof RouteNamedMap> = new Set([
  "ai_assistant-show",
]);

// The assistant's editor lives in the chat namespace, outside the mapping.
const ADDITIONAL_EDITORS: ReadonlyArray<
  readonly [keyof RouteNamedMap, AppEnum]
> = [["ai_chat-assistants-show", AppEnum.AI_ASSISTANT]];

// Editor route name → the item type it edits. The mapping also lists legacy
// `Tools::` node types; only the keys that are API types are kept.
const editorRouteTypes: ReadonlyMap<string, AppEnum> = new Map([
  ...Object.entries(itemNodeTypeToRouteMapping).flatMap(([type, routeName]) => {
    const appType = Object.values(AppEnum).find((value) => value === type);
    if (!appType || !routeName || NOT_AN_EDITOR.has(routeName)) return [];
    return [[routeName, appType] as const];
  }),
  ...ADDITIONAL_EDITORS,
]);

interface EditorMatch {
  routeName: string;
  itemType: AppEnum;
}

function editorMatch(route: RouteLocationNormalized): EditorMatch | undefined {
  for (const record of route.matched) {
    if (typeof record.name !== "string") continue;
    const itemType = editorRouteTypes.get(record.name);
    if (itemType) return { routeName: record.name, itemType };
  }
  return undefined;
}

/**
 * Keeps a colleague out of the editor of Gemeinsame Ablage material they may
 * not manage (#13233). The listing already links such material to its preview;
 * this covers the editor URL reached any other way — history, a bookmark, a
 * copied link. The preview link carries the gallery marker, so
 * `resolveItemNodeGalleryRoute` turns it into the public URL on the next pass.
 *
 * Only SPA navigations reach this guard. Editors served by
 * `Tools::VueAppsController#show` (Wortwolke, documents, pages, …) refuse a
 * full page load to anyone but the owner (and, for a pinboard, its members)
 * before the app boots.
 *
 * Material outside a Gemeinsame Ablage is left alone: other non-authors reach
 * some editors legitimately and those pages render their own view for them.
 */
export async function resolveItemEditorRoute(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  { isTeacher }: { isTeacher: boolean }
): Promise<RouteLocationRaw | undefined> {
  // Share links carry their access token as `token`.
  if (!isTeacher || to.query.public_token || to.query.token) {
    return undefined;
  }

  const editor = editorMatch(to);
  const id = (to.params as { id?: string }).id;
  if (!editor || typeof id !== "string") return undefined;

  const sameItem =
    editorMatch(from)?.routeName === editor.routeName &&
    (from.params as { id?: string }).id === id;
  if (sameItem && validatedEditors.has(from)) {
    validatedEditors.add(to);
    return undefined;
  }

  const { itemType } = editor;

  let item;
  try {
    item = await getItem(id, { objType: itemType });
  } catch {
    // The backend refuses every write anyway; let the page handle the error.
    return undefined;
  }

  if (item.permissions.manage || !item.libraryId) {
    validatedEditors.add(to);
    return undefined;
  }

  const backPath = await libraryPath(item, from);

  const publicRoute = getItemNodePublicRoute(
    itemType as string as ItemNodeItemTypeEnum
  );
  if (!publicRoute) {
    flash(FlashKindEnum.INFO, translate("sharedLibrary.editorGuard.noPreview"));
    return backPath;
  }

  return {
    name: publicRoute,
    params: { id },
    query: {
      gallery_item_type: itemType,
      back_url: new URL(backPath, window.location.origin).href,
    },
  } as RouteLocationRaw;
}
